/**
 * iter63z''''' — Site-Admin Auto-Rename Preview modal.
 *
 * Read-only dry-run: shows every bracket-slot placeholder alongside
 * the proposed real-team name pulled from API-Football standings +
 * fixtures. Admin can override any name inline, then push the diff
 * via /rename-preview/apply.
 *
 * Push-on-approval — nothing writes to the tournament until the
 * "Apply renames" button is clicked. Re-runnable: applies can be
 * repeated as many times as needed before a draft starts.
 *
 * iter63z'''''' — Generalized for any tournament with bracket_slot
 * placeholders (world_cup_ko_api, march_madness, future knockout
 * templates). Pot 3 fixture-based mapping is WCKO-only; other
 * templates get Pot 1+2 (group winners/runners-up) from standings.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trophy, RefreshCw, Save, X, AlertCircle, Pencil, Beaker, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";


const SOURCE_BADGE = {
    standings: { label: "STANDINGS", color: "bg-cyber/20 border-cyber text-cyber" },
    r32_fixture: { label: "R32 FIXTURE", color: "bg-volt/20 border-volt text-volt" },
    no_cache: { label: "NO CACHE", color: "bg-zinc-800 border-zinc-600 text-zinc-500" },
};


export default function SiteAdminRenamePreviewModal({ open, onOpenChange, tournamentId, tournamentName }) {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [data, setData] = useState(null);
    // overrides: { bracket_slot: edited_name } — only set when admin
    // explicitly types a different value than `proposed_name`.
    const [overrides, setOverrides] = useState({});

    const load = async () => {
        if (!tournamentId) return;
        setLoading(true);
        try {
            const r = await api.siteRenamePreview(tournamentId);
            setData(r);
            setOverrides({});
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't load preview");
            setData(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!open) return;
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, tournamentId]);

    const effectiveName = (row) => {
        if (overrides[row.bracket_slot] !== undefined) return overrides[row.bracket_slot];
        return row.proposed_name || "";
    };

    const apply = async () => {
        if (!data) return;
        // Build renames map: include any slot where the effective name
        // differs from the current name AND is non-blank.
        const renames = {};
        for (const row of data.preview_rows || []) {
            const next = (effectiveName(row) || "").trim();
            if (!next) continue;
            if (next === row.current_name) continue;
            renames[row.bracket_slot] = next;
        }
        if (Object.keys(renames).length === 0) {
            toast.info("Nothing to apply — no names changed.");
            return;
        }
        if (!window.confirm(
            `Apply ${Object.keys(renames).length} rename(s) to "${tournamentName}"? `
            + "Team UUIDs and draft picks are unaffected.",
        )) return;
        setSaving(true);
        try {
            const r = await api.siteRenameApply(tournamentId, renames);
            toast.success(`Applied ${r.renamed_count} rename(s).`);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Apply failed");
        } finally {
            setSaving(false);
        }
    };

    // iter63z''''''' — QA helpers.
    const injectFake = async () => {
        if (!window.confirm(
            "Inject a synthetic KO draw (32 plausible WC 2026 teams + 16 R32 fixtures) "
            + "into the API-Football cache for this rivalry/season? Used to validate the "
            + "rename flow end-to-end before real data arrives. "
            + "The background poller will leave it alone — clear it manually via 'Clear fake' "
            + "or by clicking Refresh on the tournament when you're ready for real data.",
        )) return;
        setSaving(true);
        try {
            const r = await api.siteInjectFakeKoDraw(tournamentId);
            toast.success(`Injected ${r.team_count} teams + ${r.match_count} fixtures.`);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Inject failed");
        } finally {
            setSaving(false);
        }
    };
    const clearFake = async () => {
        if (!window.confirm(
            "Wipe the synthetic KO draw injection from the API-Football cache? "
            + "Real data (if any) is NOT touched — this only clears entries marked is_fake_injection.",
        )) return;
        setSaving(true);
        try {
            const r = await api.siteClearFakeKoDraw(tournamentId);
            toast.success(r.message || "Fake draw cleared.");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Clear failed");
        } finally {
            setSaving(false);
        }
    };

    const totalRows = (data?.preview_rows || []).length;
    const mapped = (data?.preview_rows || []).filter(r => r.proposed_name).length;
    const unmapped = totalRows - mapped;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="rename-preview-modal"
                className="max-w-4xl bg-ink border-2 border-cyber/40 max-h-[92dvh] overflow-y-auto"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 font-display font-extrabold uppercase text-cyber">
                        <Trophy className="w-5 h-5" /> Auto-Rename Preview
                        <span className="text-zinc-500 text-xs font-normal normal-case ml-1">{tournamentName}</span>
                    </DialogTitle>
                </DialogHeader>

                <div className="text-xs text-zinc-400 leading-relaxed space-y-1 mb-3">
                    <p>
                        Read-only dry-run. We pull the cached API-Football standings + R32 fixtures
                        and show what the materialize would set each placeholder team's name to.
                        Edit any row inline if the source data is wrong, then click <b>Apply renames</b>.
                    </p>
                    <p className="text-zinc-500">
                        Only the <code className="text-zinc-300">name</code> field is touched. UUIDs, seed,
                        bracket_slot, matches, and draft picks stay put. Re-runnable until a draft starts.
                    </p>
                </div>

                {loading ? (
                    <p className="text-zinc-500 text-sm py-8 text-center" data-testid="rename-preview-loading">Loading…</p>
                ) : !data ? (
                    <p className="text-zinc-500 text-sm py-8 text-center">No data.</p>
                ) : (
                    <>
                        <div className="flex flex-wrap items-center gap-2 mb-3 text-[11px]">
                            <span className={`pill ${data.cache_present ? "bg-cyber/20 border-cyber text-cyber" : "bg-zinc-800 border-zinc-600 text-zinc-500"}`}>
                                {data.cache_present ? `CACHE OK · ${data.cache_team_count} teams · ${data.cache_match_count} matches` : "NO CACHE"}
                            </span>
                            {data.awaiting_ko_draw && (
                                <span className="pill bg-volt/20 border-volt text-volt">AWAITING KO DRAW</span>
                            )}
                            <span className="pill bg-zinc-900 border-zinc-700 text-zinc-300">
                                {mapped}/{totalRows} mapped
                            </span>
                            {unmapped > 0 && (
                                <span className="pill bg-blaze/20 border-blaze text-blaze">
                                    {unmapped} unmapped
                                </span>
                            )}
                            <button
                                type="button"
                                data-testid="rename-preview-refresh-btn"
                                onClick={load}
                                disabled={loading || saving}
                                className="brutal-btn-ghost text-[10px] py-1 px-2 ml-auto"
                            >
                                <RefreshCw className="w-3 h-3" /> Refresh
                            </button>
                        </div>

                        {data.message && (
                            <p className="text-[11px] text-zinc-400 mb-3 italic">{data.message}</p>
                        )}

                        <div className="space-y-1 mb-4">
                            <div className="grid grid-cols-[60px_1fr_1fr_90px] gap-2 text-[10px] uppercase tracking-widest text-zinc-500 px-2 py-1 border-b border-zinc-800">
                                <span>Slot</span>
                                <span>Current name</span>
                                <span>Proposed → (editable)</span>
                                <span>Source</span>
                            </div>
                            {(data.preview_rows || []).map((row) => {
                                const eff = effectiveName(row);
                                const changed = eff && eff.trim() !== row.current_name;
                                const sourceMeta = SOURCE_BADGE[row.source] || null;
                                return (
                                    <div
                                        key={row.bracket_slot}
                                        data-testid={`rename-preview-row-${row.bracket_slot}`}
                                        className={`grid grid-cols-[60px_1fr_1fr_90px] gap-2 items-center px-2 py-1.5 rounded ${changed ? "bg-volt/5 border border-volt/30" : "hover:bg-zinc-900/50"}`}
                                    >
                                        <span className="text-xs font-bold text-cyber">{row.bracket_slot}</span>
                                        <span className="text-xs text-zinc-400 truncate" title={row.current_name}>
                                            {row.current_name}
                                        </span>
                                        <div className="flex items-center gap-1 min-w-0">
                                            <Pencil className="w-3 h-3 text-zinc-600 shrink-0" />
                                            <input
                                                type="text"
                                                data-testid={`rename-preview-input-${row.bracket_slot}`}
                                                value={eff}
                                                placeholder={row.proposed_name ? "" : "— no mapping yet —"}
                                                onChange={(e) => setOverrides(o => ({
                                                    ...o,
                                                    [row.bracket_slot]: e.target.value,
                                                }))}
                                                className="bg-zinc-950 border border-zinc-700 rounded px-2 py-1 text-xs text-white w-full min-w-0 focus:outline-none focus:border-cyber"
                                            />
                                        </div>
                                        <div className="text-right">
                                            {sourceMeta ? (
                                                <span className={`pill text-[9px] py-0 px-1.5 ${sourceMeta.color}`}>
                                                    {sourceMeta.label}
                                                </span>
                                            ) : (
                                                <span className="pill bg-zinc-800 border-zinc-600 text-zinc-500 text-[9px] py-0 px-1.5">—</span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {unmapped > 0 && (
                            <div className="flex items-start gap-2 brutal-card-light p-2 mb-3 border-blaze/40">
                                <AlertCircle className="w-4 h-4 text-blaze mt-0.5 shrink-0" />
                                <p className="text-[11px] text-zinc-300">
                                    {unmapped} slot(s) couldn't be mapped automatically. This usually
                                    means the group stage hasn't finished yet or API-Football hasn't
                                    published the R32 draw. You can still type a name manually if you know it.
                                </p>
                            </div>
                        )}

                        <div className="flex flex-wrap gap-2 justify-end pt-2 border-t border-zinc-800">
                            <button
                                type="button"
                                data-testid="rename-preview-inject-fake-btn"
                                onClick={injectFake}
                                disabled={saving || loading}
                                className="brutal-btn-ghost text-xs py-2 px-3 mr-auto border-cyber/40 text-cyber"
                                title="QA helper — write 32 plausible WC 2026 teams + 16 R32 fixtures into the API-Football cache so the rename flow can be exercised end-to-end before real data lands.">
                                <Beaker className="w-3 h-3" /> Inject fake KO draw
                            </button>
                            <button
                                type="button"
                                data-testid="rename-preview-clear-fake-btn"
                                onClick={clearFake}
                                disabled={saving || loading}
                                className="brutal-btn-ghost text-xs py-2 px-3 text-zinc-500"
                                title="Clear the synthetic injection. Real data is never touched — only is_fake_injection entries get removed.">
                                <Trash2 className="w-3 h-3" /> Clear fake
                            </button>
                            <button
                                type="button"
                                data-testid="rename-preview-cancel-btn"
                                onClick={() => onOpenChange?.(false)}
                                disabled={saving}
                                className="brutal-btn-ghost text-xs py-2 px-3"
                            >
                                <X className="w-3 h-3" /> Close
                            </button>
                            <button
                                type="button"
                                data-testid="rename-preview-apply-btn"
                                onClick={apply}
                                disabled={saving || loading}
                                className="brutal-btn text-xs py-2 px-3 bg-volt text-black border-black shadow-brutal disabled:opacity-50"
                            >
                                <Save className="w-3 h-3" /> {saving ? "Applying…" : "Apply renames"}
                            </button>
                        </div>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}
